import { useCallback, useContext, useState } from "react";
import { CanonicalBusinessEvent } from "../types/cbes";
import { DevUserContext, StageContext } from "../contexes/OrderIdContext";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import { getDeferralIds } from "../helpers/cbeSummaryUtils";
import {
  createFlashUILink,
  createHerdLink,
  createRaceLink,
} from "../helpers/linkUtils";
import {
  clearModalParam,
  readInitialModal,
  setModalParam,
} from "../helpers/modalUrlUtils";

interface CbeLink {
  label: string;
  href: string;
}

export function usePublishStatus(cbe: CanonicalBusinessEvent) {
  const cbeV = String(cbe.idempotenceVersion);
  const [isModalVisible, setIsModalVisible] = useState(() => {
    const { modal, cbeId, cbeV: v } = readInitialModal();
    return modal === "cbe-error" && cbeId === cbe.idempotenceId && v === cbeV;
  });
  const [expandAll, setExpandAll] = useState(true);
  const stage = useContext(StageContext);
  const devUser = useContext(DevUserContext);
  const { marketplaceId } = useMarketplaceId();

  const handleModalOpen = useCallback(() => {
    setIsModalVisible(true);
    setModalParam("cbe-error", { cbeId: cbe.idempotenceId, cbeV });
  }, [cbe.idempotenceId, cbeV]);
  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    clearModalParam();
  }, []);
  const handleExpandAll = useCallback(() => setExpandAll((prev) => !prev), []);

  const getCbeLinks = useCallback((): CbeLink[] => {
    if (cbe.destination === "FLARE") {
      return [
        {
          label: "Flash UI",
          href: createFlashUILink(
            marketplaceId,
            cbe.idempotenceId,
            stage,
            devUser
          ),
        },
      ];
    }

    if (cbe.destination === "RACE") {
      const { revenueDeferralId, royaltyDeferralId } = getDeferralIds(cbe);

      if (
        revenueDeferralId &&
        royaltyDeferralId &&
        revenueDeferralId !== royaltyDeferralId
      ) {
        return [
          {
            label: "Revenue Deferral",
            href: createRaceLink(
              marketplaceId,
              revenueDeferralId,
              stage,
              devUser
            ),
          },
          {
            label: "Royalty Deferral",
            href: createRaceLink(
              marketplaceId,
              royaltyDeferralId,
              stage,
              devUser
            ),
          },
        ];
      }

      if (revenueDeferralId || royaltyDeferralId) {
        return [
          {
            label: "Deferral",
            href: createRaceLink(
              marketplaceId,
              revenueDeferralId || royaltyDeferralId || "",
              stage,
              devUser
            ),
          },
        ];
      }

      return [
        {
          label: "Order ID",
          href: createRaceLink(marketplaceId, cbe.orderId, stage, devUser),
        },
      ];
    }

    return [{ label: "Unknown", href: "#" }];
  }, [cbe, marketplaceId, stage, devUser]);

  const herdLink =
    cbe.destination === "FLARE" &&
    cbe.businessNamespace &&
    cbe.idempotenceId &&
    cbe.idempotenceVersion != null
      ? createHerdLink(cbe, marketplaceId, stage)
      : null;

  return {
    isModalVisible,
    expandAll,
    handleModalOpen,
    handleModalClose,
    handleExpandAll,
    getCbeLinks,
    herdLink,
  };
}
